<template>
  <div>
    <smd-one-col-layout class="main full-width"
                        :content-styles="computedHideOverflow">
      <full-screen-loader class="main__loader" v-if="loading"/>
      <div :style="mainStyle">
        <banner-incoming-call v-for="(session, index) in getActiveSessions"
                              class="banner"
                              :key="index"
                              :doctor="session._doctor"
                              @call-accept="handleCallAccept(session)"
                              @call-later-pressed="handleCallPressedLater"/>
        <banner-od-accepted v-if="acceptedOd"
                            :booking-id="acceptedOd"
                            class="banner" />
        <p v-if="error" class="error">{{ error }}</p>
        <router-view v-if="!loading" :class="{'m-t-20': error}"/>
      </div>
      <smd-bottom-nav v-if="computedShowBottomNav"
                      slot="bottom-nav"
                      :options="navItems"/>
    </smd-one-col-layout>
    <div class="app__content__overlay"
         v-if="!loading"
         v-show="showOverlay">
      <div class="overflow-container">
        <transition enter-active-class="zoom-enter-active"
                    leave-active-class="zoom-leave-active">
          <router-view name="overlay"/>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
  import {CALL_RINGTONE_URL} from '@/constants';
  import {extractDateStringOnIsoString} from '@/utils';
  import {getAccountWithDetailsService} from '@/services/account';
  import {MedicineService} from '@/services/medicine';
  import {logout} from '@/utils/logout';
  import {mapActions, mapMutations} from 'vuex';
  import {mapGetters} from 'vuex';
  import {FilesService} from '@/services/files';
  import {EducationService} from '@/services/education';
  import BannerIncomingCall from '@/components/banners/BannerIncomingCall';
  import BannerOdAccepted from '@/components/banners/banner-od-accepted';
  import {navMobile} from '@/reference/nav-items';
  import {PageTypes} from '@/reference';
  import socketMixin from '@/mixins/socket';
  import {getAccessToken} from '@/utils/access-token';
  import {MODAL_LOCAL_STORAGE_KEYS} from "@/reference/modal-instruction-items";
  import Routes from '@/router/routes';
  import {transformReceivedLabResult} from "@/utils/lab-tests";
  import {
    GETTER_FILES,
    GETTER_TOKENS,
    GETTER_ACTIVE_SESSIONS,
    GETTER_DOCTOR_PATIENT_PARTNER,
    GETTER_MEDICINE_ORDERS,
    GETTER_UNREAD_MESSAGES,
    GETTER_DOCTOR_PATIENT_PARTNER_BY_TOKEN,
    ACTION_EDIT_ACCOUNT,
    ACTION_SET_MEDICINE_STATE,
    ACTION_REFRESH_APPOINTMENTS,
    ACTION_SET_FILES_STATE,
    ACTION_ADD_UNREAD_MESSAGE_COUNT,
    ACTION_SET_ORDER_LAST_MESSAGE,
    ACTION_ADD_DOCTOR_LATEST_MESSAGES,
    ACTION_SET_BILLS_STATE,
    ACTION_REFRESH_ACTIVE_SESSIONS,
    ACTION_GET_OD_BOOKINGS,
    ROUTING_SHOW_OVERLAY,
    ROUTING_QUERY,
  } from '@/store/types';
  import {
    setDoctorSocketMessageType,
  } from '@/utils/messages';
  import {
    SOCKET_USER,
    SOCKET_USER_DOCTOR,
    SOCKET_EVENT,
  } from '@/constants/socket';
  import {HANDLE_SHARED_FILE} from '@/constants/bus';
  import {bus} from '@/main';
  import titleMixin from "@/mixins/title-mixin";
  import {MUTATE_ROUTE_QUERY} from "../store/types";

  export default {
    name: 'Main',
    components: {
      BannerIncomingCall,
      BannerOdAccepted,
    },
    mixins: [
      socketMixin,
      titleMixin,
    ],
    data() {
      return {
        error: null,
        intervalReference: null,
        sessionInterval: null,
        ondemandInterval: null,
        loading: false,
        acceptedOd: null,
        socketListeners: [
          {
            event: SOCKET_EVENT.RECONNECT,
            callback: () => {
              this.emitJoinSockets();
            },
          },
          {
            event: SOCKET_EVENT.TOKEN_PING,
            callback: (data) => {
              if (data.user === SOCKET_USER_DOCTOR) {
                this.$socket.emit(SOCKET_EVENT.TOKEN_PONG, {
                  ...data,
                  ponguser: SOCKET_USER,
                });
              }
            },
          },
          {
            event: SOCKET_EVENT.REQUEST_START,
            callback: () => {
              this.fetchActiveSessions();
              this.playRingtone();
            }
          },
          {
            event: SOCKET_EVENT.ORDER_MESSAGE,
            callback: this.appendNewOrderMessage,
          },
          {
            event: SOCKET_EVENT.ROOM_MESSAGE,
            callback: this.appendNewDoctorMessage,
          },
          {
            event: SOCKET_EVENT.SHARED_FILE,
            callback: this.appendNewDoctorMessage,
          },
          {
            event: SOCKET_EVENT.OD_BOOKING_ACCEPT,
            callback: this.onODBookingAccept,
          },
        ],
      };
    },
    computed: {
      ...mapGetters({
        allFiles: GETTER_FILES,
        tokens: GETTER_TOKENS,
        getDoctorPatientPair: GETTER_DOCTOR_PATIENT_PARTNER,
        getActiveSessions: GETTER_ACTIVE_SESSIONS,
        routeQueryState: ROUTING_QUERY,
        showOverlay: ROUTING_SHOW_OVERLAY,
        orders: GETTER_MEDICINE_ORDERS,
        unreadCountsList: GETTER_UNREAD_MESSAGES,
        getterDoctorPatientByToken: GETTER_DOCTOR_PATIENT_PARTNER_BY_TOKEN,
      }),
      computedHideOverflow() {
        return {'hide-overflow': this.onChatWindow}
      },
      computedShowBottomNav() {
        return this.$isMobile &&
          this.$route.name !== Routes.ACCOUNT_ROUTE &&
          !this.onChatWindow &&
          this.$route.name !== Routes.PROGRAM_ROOT;
      },
      mainStyle() {
        const styles = {};

        if (this.onChatWindow) {
          styles.marginBottom = 0;
        }

        return styles;
      },
      onChatWindow() {
        return [
          Routes.CHAT_ROUTE,
          Routes.ORDER_CHAT_ROUTE,
        ].includes(this.$route.name);
      },
      navItems() {
        const hasUnreadMessage = Object.values(this.unreadCountsList).some((unreadCount) => unreadCount > 0);
        const navItems = [...navMobile];

        if (hasUnreadMessage) {
          const messageTabIndex = navItems.findIndex(({id}) => id === Routes.MESSAGES_ROUTE);
          this.$set(navItems, messageTabIndex, {...navItems[messageTabIndex], notifs: 1});
        }

        return navItems;
      },
    },
    created() {
      this.initialize();

      bus.$once(HANDLE_SHARED_FILE, ({uploadid, isForNewToken}) => this.handleSharedFile(uploadid, isForNewToken));
    },
    destroyed() {
      clearInterval(this.intervalReference);
      clearInterval(this.sessionInterval);
      clearInterval(this.ondemandInterval);
    },
    methods: {
      ...mapMutations({
        updateRouteQueryState: MUTATE_ROUTE_QUERY,
      }),
      ...mapActions({
        editAccount: ACTION_EDIT_ACCOUNT,
        setMedicineState: ACTION_SET_MEDICINE_STATE,
        refreshAppointments: ACTION_REFRESH_APPOINTMENTS,
        fetchActiveSessions: ACTION_REFRESH_ACTIVE_SESSIONS,
        setFilesState: ACTION_SET_FILES_STATE,
        addDoctorLastMessage: ACTION_ADD_DOCTOR_LATEST_MESSAGES,
        addUnreadMessageCount: ACTION_ADD_UNREAD_MESSAGE_COUNT,
        setOrderLastMessage: ACTION_SET_ORDER_LAST_MESSAGE,
        setBillsState: ACTION_SET_BILLS_STATE,
        fetchOnDemandBookings: ACTION_GET_OD_BOOKINGS,
      }),
      async initialize() {
        this.loading = true;

        try {
          const accountData = await getAccountWithDetailsService();

          this.editAccount({
            firstname: accountData.firstname,
            middlename: accountData.middlename,
            lastname: accountData.lastname,
            birthdate: extractDateStringOnIsoString(accountData.birthdate),
            mobilenumber: accountData.mobilenumber,
            email: accountData.email,
            tokens: accountData.tokens,
          });
          this.fetchAppointments();
          await this.fetchMedicineInitialData();
          this.startIntervalAppointmentsRefresh();
          this.emitJoinSockets();
          await this.fetchActiveSessions();
          this.startIntervalCheckSessions();
          this.setLocalStorageForModalInstructions();
          await this.fetchOnDemandBookings();
          this.startIntervalCheckODBookings();

          if (this.routeQueryState && this.routeQueryState.token) {
            await this.fetchFiles();
          }

          this.loading = false;
        } catch (error) {
          this.loading = false;
          this.error = 'Failed to fetch patient account. Please try refreshing the page.';
        }
      },
      setLocalStorageForModalInstructions() {
        const items = [
          MODAL_LOCAL_STORAGE_KEYS.PHARMACY_ORDERS,
          MODAL_LOCAL_STORAGE_KEYS.HIPRES
        ];
        const checkIfNotExist = (item) => {
          return localStorage.getItem(item) === null
        }

        items.forEach(item => {
          if (checkIfNotExist(item))
            localStorage.setItem(item, '1')
        })
      },
      computedActiveIcon(item) {
        const name = item.to.name;
        const activeRoute = this.$route.meta.root || this.$route.name;
        return activeRoute === name ? item.iconSelected : item.icon;
      },
      startIntervalCheckSessions() {
        this.sessionInterval = setInterval(() => {
          this.fetchActiveSessions().catch(() => {});
        }, 60000);
      },
      startIntervalCheckODBookings() {
        this.ondemandInterval = setInterval(() => {
          this.fetchOnDemandBookings().catch(() => {});
        }, 15000);
      },
      playRingtone() {
        this.$options.ringtone.play();
      },
      stopRingtone() {
        this.$options.ringtone.pause();
        this.$options.ringtone.currentTime = 0;
      },
      handleCallAccept(session) {
        this.stopRingtone();
        
        let source = `${window.location.origin}/portal/`;
        const matchedPartner = this.getterDoctorPatientByToken[session.token];

        if (matchedPartner) {
          source = `${source}messages/${matchedPartner.doctor.usertag}/${matchedPartner.patient.id}`;
        }

        const callUrl = `${process.env.VUE_APP_SOCKET}/groupcalls/calls/?accesstoken=${session.accesstoken}&source=${source}`;

        window.location.href = callUrl;
      },
      handleCallPressedLater() {
        this.stopRingtone();
      },
      handleSharedFile(uploadid, isForNewToken) {
        const resMatch = isForNewToken
          ? this.fetchFiles() //should also fetch all other files sent by the doctor before claiming the profile
          : Promise.resolve();

        resMatch
          .then(() => {
            const match = this.allFiles.find(f => f.uploadid === uploadid);
            if (match) {
              this.$router.push({
                name: Routes.PREVIEW_FILE,
                params: {
                  file: [match],
                  title: this.computedTitle(match),
                  filesView: true,
                },
              });
            }

            this.updateRouteQueryState(null);
          });
      },
      fetchAppointments() {
        this.error = null;

        try {
          this.refreshAppointments();
        } catch (error) {
          this.error = 'Failed to fetch your consultations. Please try refreshing the page.';
        }
      },
      async fetchFiles() {
        this.error = null;
        this.setFilesState({fetching: true});

        try {
          const requests = this.tokens.map(({accesstoken}) => FilesService.getFiles(accesstoken));
          const responses = await Promise.all(requests);

          const {data: labResultsData} = await FilesService.getLabResults();
          const {data: pxMaterialsData} = await EducationService.getPxMaterials();

          const files = [];

          responses.forEach((response, index) => {
            const {accesstoken} = this.tokens[index];

            response.data.forEach((item) => {
              item.token = accesstoken;
              item.doctorname = this.getDoctorPatientPair[accesstoken].doctor._fullName;
              item.patientname = this.getDoctorPatientPair[accesstoken].patient.name;

              files.push(item);
            });
          });

          labResultsData.forEach((labResult) => {
            const patientTokenObj = this.tokens.find(({token}) => labResult.token === token);

            if (patientTokenObj) {
              files.push(transformReceivedLabResult(labResult, this.getDoctorPatientPair[patientTokenObj.accesstoken]));
            } else if (labResult.sourcelab) {
              files.push({...labResult, pagetype: PageTypes.__labresults});
            } else {
              files.push(labResult);
            }
          });

          const {result: pxResult, pxmaterials} = pxMaterialsData;
          if (pxResult === 1) {
            pxmaterials.forEach(material => files.push({...material, pagetype: PageTypes.educ}));
          }

          this.setFilesState({fetching: false, files});
        } catch (error) {
          this.error = 'Failed to fetch your files. Please try refreshing the page.';
        }
      },
      async fetchMedicineInitialData() {
        this.error = null;
        this.setMedicineState({loading: true});

        try {
          const {data: {orders}} = await MedicineService.getOrders();
          const {data: {partners}} = await MedicineService.getPartners();

          this.setMedicineState({
            orders,
            partners,
            loading: false,
          });
        } catch (error) {
          this.setMedicineState({loading: false});
          this.error = 'Failed to fetching initial medicine data. Please try refreshing the page.';
        }
      },
      startIntervalAppointmentsRefresh() {
        this.intervalReference = setInterval(() => {
          this.fetchAppointments();
        }, 60000);
      },
      emitJoinSockets() {
        this.error = null;

        this.$socket.emit(SOCKET_EVENT.JOIN_ACCOUNT, {
          token: getAccessToken(),
          user: SOCKET_USER,
        });

        if (this.tokens.length) {
          this.tokens.forEach(({accesstoken, _refId}) => {
            this.$socket.emit(SOCKET_EVENT.JOIN, {
              token: accesstoken,
              refId: _refId,
              user: SOCKET_USER,
            });
          });
        }
      },
      appendNewOrderMessage(message) {
        if (message.from !== '1') {
          const order = this.orders.find(({orderid}) => orderid === message.orderid);

          if (!order) return;

          const alreadyReceived = order.lastOrderMessage && (order.lastOrderMessage._id || order.lastOrderMessage.id) === message.id;

          if (!alreadyReceived) {
            this.setOrderLastMessage(message);
            this.addUnreadMessageCount(message.orderid);
          }
        } else {
          this.setOrderLastMessage(message);
        }
      },
      appendNewDoctorMessage(message) {
        const tokenObj = this.tokens.find(({token}) => token === message.token);
        const {localStorageKey} = this.getDoctorPatientPair[tokenObj.accesstoken];

        setDoctorSocketMessageType(message);

        this.addDoctorLastMessage({
          [localStorageKey]: message,
        });

        if (message.type === 'pxbill') {
          this.setBillsState({initialized: false});
        }

        const onCurrentChatRoute = this.$route.params[Routes.MESSAGE_DOCTOR_PARAM] === tokenObj.doctor.usertag &&
          this.$route.params[Routes.MESSAGE_PATIENT_ID_PARAM] === tokenObj.patient.id;

        if (message.from !== 0 && !onCurrentChatRoute) {
          this.addUnreadMessageCount(localStorageKey);
        }
      },
      onODBookingAccept(data) {
        this.acceptedOd = data.requestid;
      },
      logout,
    },
    ringtone: new Audio(CALL_RINGTONE_URL),
  }
</script>

<style lang="scss">
  $navDesktopWidth: 160px;

  .main {
    .banner {
      position: fixed;
      z-index: 300000;
      top: 0;
      left: 0;
      right: 0;
    }

    p.error {
      position: relative;
      color: color(white, text);
      text-align: center;
      background-color: color(red, tags);
      padding: space(xs);
      z-index: 100;
    }

    .smd-bottom-nav {
      .ns-icon-calendar {
        color: color(gray-400);
        line-height: 39px;
        font-size: 24px;
      }

      .active {
        .ns-icon-calendar {
          color: color(purple);
        }
      }
    }
  }

  .main__loader {
    position: fixed !important;
  }
</style>
